import {
  ActionBar,
  Button,
  Checkbox,
  Dropdown,
  DropdownMenu,
  EmptyState,
  ImportIcon,
  LazyLoader,
  SubjectIllustration,
  Tooltip,
} from '@bp/ui-components';
import classNames from 'classnames';
import { getFragmentData, graphql } from 'client/gql';
import {
  ForeignSyncMetaCreateInput,
  ForeignSyncMetasFieldsFragment,
  ForeignSyncMetaUpdateInput,
} from 'client/gql/graphql';
import { BpCard } from 'components/BpCard/BpCard';
import { Form, Formik } from 'formik';
import { SettingsImportPlanungSubpageOutletContext } from 'pages/Settings/subpages/SettingsImportPlanungSubpage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useMutation } from 'urql';
import { backendApi } from 'utils/backendApi';
import { createForeignRefId, SyncMaster } from 'utils/create-foreignRefId';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import {
  CreateForeignSyncMetaDocument,
  ForeignSyncMetasFieldsDocument,
  UpdateForeignSyncMetaDocument,
} from '../queries/queries';
import styles from './_PlanungImportForms.module.scss';
import { PlanungImportSubjectsFormRow } from './PlanungImportSubjectsFormRow';

const PlanungImportSubjectsFormFragment = graphql(`
  fragment PlanungImportSubjectsFormFields on Query {
    subjects(options: { sort: [{ name: ASC }] }, where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      name
    }
    globalSubjects {
      __typename
      name: subject
    }
  }
`);

type SubjectModel = {
  uuid: string;
  selected: boolean;
  name: string;
  connectGlobalName: string | null;
  connectSubjectUuid: string | null;
  connected: boolean;
  ignore: boolean;
};

export const PlanungImportSubjectsForm = () => {
  const { t } = useTranslation();

  const { bpData, planungData, refetch, planungSchoolyear, bpSchoolyear } =
    useOutletContext<SettingsImportPlanungSubpageOutletContext>();

  const metaFragment = getFragmentData(ForeignSyncMetasFieldsDocument, bpData);
  const formFragment = getFragmentData(PlanungImportSubjectsFormFragment, bpData);

  const foreignSyncMetas: Pick<ForeignSyncMetasFieldsFragment, 'foreignSyncMetas'>['foreignSyncMetas'] | undefined =
    useMemo(
      () => metaFragment?.foreignSyncMetas.filter((fsm) => !fsm.syncTarget || fsm.syncTarget?.__typename === 'Subject'),
      [metaFragment],
    );

  const subjects = formFragment?.subjects ?? [];
  const globalSubjects = formFragment?.globalSubjects ?? [];
  const planungSubjects = planungData?.subjects ?? [];

  const [, createForeignSyncMeta] = useMutation(CreateForeignSyncMetaDocument);
  const [, updateForeignSyncMeta] = useMutation(UpdateForeignSyncMetaDocument);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState<string | null>(null);
  const [subjectModel, setSubjectModel] = useState<SubjectModel[]>([]);

  const handleImport = async () => {
    const response = await backendApi.post(
      `/planung/import/entities/subjects`,
      JSON.stringify({
        planungSchoolyear,
        bpSchoolyear,
      }),
    );
    return JSON.stringify(await response.json());
  };

  const handleSubmit = async () => {
    const selectedSubjects = subjectModel.filter((s) => s.selected);

    // validate if all global subjects are set
    if (selectedSubjects.filter((s) => !s.connectGlobalName).length > 0) {
      return;
    }

    setLoading(true);

    const createInput = (subject: SubjectModel, foreignRefId: string): ForeignSyncMetaCreateInput => ({
      foreignRefId: foreignRefId,
      syncSource: SyncMaster.planung,
      ignore: subject.ignore,
      globalSubject: subject.connectGlobalName,
      syncTarget: subject.connectSubjectUuid
        ? { Subject: { connect: { where: { node: { uuid: subject.connectSubjectUuid } } } } }
        : undefined,
    });

    const updateInput = (subject: SubjectModel): ForeignSyncMetaUpdateInput => ({
      ignore: subject.ignore,
      globalSubject: subject.connectGlobalName,
      syncTarget: subject.connectSubjectUuid
        ? { Subject: { disconnect: {}, connect: { where: { node: { uuid: subject.connectSubjectUuid } } } } }
        : { Subject: { disconnect: {} } },
    });

    let errors: number = 0;

    for (const subject of selectedSubjects) {
      const foreignRefId = createForeignRefId(SyncMaster.planung, subject.uuid);
      const hasForeignSyncMeta = !!foreignSyncMetas?.find((p) => p.foreignRefId === foreignRefId);

      if (hasForeignSyncMeta) {
        const { error } = await updateForeignSyncMeta({
          update: updateInput(subject),
          where: { foreignRefId: foreignRefId },
        });
        error && errors++;
      } else {
        const { error } = await createForeignSyncMeta({ input: createInput(subject, foreignRefId) });
        error && errors++;
      }
    }

    if (errors > 0) {
      showUserDbErrorToast();
    } else {
      await handleImport();
      showSuccessToast(t('planungImport.success'));
    }

    refetch();
    setLoading(false);
  };

  function handleSelect(uuid: string, select: boolean) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      selected: ps.uuid === uuid ? select : ps.selected,
      connectGlobalName: ps.uuid === uuid && !ps.connectGlobalName && !select ? null : ps.connectGlobalName,
      connectSubjectUuid: ps.uuid === uuid && !ps.connectSubjectUuid && !select ? null : ps.connectSubjectUuid,
    }));
    setSubjectModel(newModel);
  }

  function handleGlobalSelect(uuid: string, connectGlobalName: string | null) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      connectGlobalName: ps.uuid === uuid ? connectGlobalName : ps.connectGlobalName,
    }));
    setSubjectModel(newModel);
  }

  function handleSubjectSelect(uuid: string, connectSubjectUuid: string | null) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      connectSubjectUuid: ps.uuid === uuid ? connectSubjectUuid : ps.connectSubjectUuid,
    }));
    setSubjectModel(newModel);
  }

  function handleSelectAll(select: boolean) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      selected: filteredModel.includes(ps) ? select : ps.selected,
      connectGlobalName: !ps.connectGlobalName && !select ? null : ps.connectGlobalName,
      connectSubjectUuid: !ps.connectSubjectUuid && !select ? null : ps.connectSubjectUuid,
    }));
    setSubjectModel(newModel);
  }

  function handleSelectImported(select: boolean) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      selected: filteredModel.includes(ps) && ps.connected ? select : ps.selected,
      connectGlobalName: !ps.connectGlobalName && !select ? null : ps.connectGlobalName,
      connectSubjectUuid: !ps.connectSubjectUuid && !select ? null : ps.connectSubjectUuid,
    }));
    setSubjectModel(newModel);
  }

  function handleSelectNotImported(select: boolean) {
    const newModel = subjectModel.map((ps) => ({
      ...ps,
      selected: filteredModel.includes(ps) && !ps.connected ? select : ps.selected,
      connectGlobalName: !ps.connectGlobalName && !select ? null : ps.connectGlobalName,
      connectSubjectUuid: !ps.connectSubjectUuid && !select ? null : ps.connectSubjectUuid,
    }));
    setSubjectModel(newModel);
  }

  const filteredModel = subjectModel
    .filter((s) => (!search ? true : s.name.toLowerCase().includes(search?.toLowerCase())))
    .sort((a, b) => {
      // imported
      if (a.connected !== b.connected) {
        return a.connected ? 1 : -1;
      }
      // sort by name
      return naturalStringSort(a.name, b.name);
    });

  const availableGlobalSubjects: { uuid: string; name: string }[] = useMemo(() => {
    return globalSubjects.map((gs) => ({ uuid: gs.name, name: gs.name }));
  }, [globalSubjects]);

  const availableSubjects: { uuid: string; name: string }[] = useMemo(() => {
    return subjects.map((s) => ({ uuid: s.uuid, name: s.name ?? '' }));
  }, [subjects]);

  useEffect(() => {
    const model: SubjectModel[] = planungSubjects
      .sort((a, b) => naturalStringSort(a.name, b.name))
      .map((ps) => {
        const fsm = foreignSyncMetas?.find(
          (fsm) => fsm.foreignRefId === createForeignRefId(SyncMaster.planung, ps.uuid),
        );
        const connected = !!fsm && !!fsm.syncTarget && fsm.syncTarget.__typename === 'Subject';
        const preSelectGlobal = globalSubjects.find((gs) => gs.name === ps.name);
        const preSelectSubject = subjects.find((s) => s.name === ps.name);
        return {
          uuid: ps.uuid,
          selected: !connected,
          name: ps.name ?? '',
          connectGlobalName: fsm?.globalSubject ? fsm.globalSubject : (preSelectGlobal?.name ?? null),
          connectSubjectUuid: connected ? (fsm.syncTarget?.uuid ?? null) : (preSelectSubject?.uuid ?? null),
          connected: connected,
          ignore: !!fsm?.ignore && fsm.ignore,
        };
      });
    setSubjectModel(model);
  }, [planungSubjects, subjects, globalSubjects, foreignSyncMetas]);

  return (
    <Formik initialValues={{}} onSubmit={handleSubmit}>
      {() => (
        <Form className={styles['planung-import-form']}>
          {loading ? (
            <LazyLoader size='xxl' borderRadius='m' forceHeight='25vh' />
          ) : (
            <BpCard noPadding header={{ headline: t('planungImport.importSubjects') }}>
              <ActionBar
                isOnWhite
                onGlobalFilterChange={(value) => setSearch(value)}
                className={styles['action-bar']}
                extendedActionsLeft={
                  <div className={styles.overview}>
                    {t('planungImport.importOverview', {
                      sum: subjectModel.filter((s) => s.selected).length,
                      total: filteredModel.length,
                    })}
                  </div>
                }
                extendedActionsRight={
                  <>
                    <Dropdown trigger={<Button hierarchy='tertiary'>{t('common.selection')}</Button>}>
                      <DropdownMenu
                        data={[
                          { label: t('planungImport.selectNotImported'), onClick: () => handleSelectNotImported(true) },
                          {
                            label: t('planungImport.deselectNotImported'),
                            onClick: () => handleSelectNotImported(false),
                          },
                          { type: 'ruler' },
                          { label: t('planungImport.selectImported'), onClick: () => handleSelectImported(true) },
                          { label: t('planungImport.deselectImported'), onClick: () => handleSelectImported(false) },
                          { type: 'ruler' },
                          { label: t('common.selectAll'), onClick: () => handleSelectAll(true) },
                          { label: t('planungImport.deselectAll'), onClick: () => handleSelectAll(false) },
                        ]}
                      />
                    </Dropdown>
                    <Button
                      type='submit'
                      icon={<ImportIcon />}
                      disabled={
                        subjectModel.filter((s) => s.selected).length === 0 ||
                        subjectModel.filter((s) => s.selected).some((s) => !s.connectGlobalName)
                      }
                    >
                      {subjectModel.filter((s) => s.selected).some((s) => !s.connectGlobalName) ? (
                        <Tooltip content={t('planungImport.noGlobalSubjectsHint')}>
                          {t('planungImport.createSubjects')}
                        </Tooltip>
                      ) : (
                        t('planungImport.createSubjects')
                      )}
                    </Button>
                  </>
                }
              />
              <div className={classNames(styles.form, styles.subjects)}>
                {filteredModel.length === 0 ? (
                  <EmptyState icon={<SubjectIllustration />} title={t('subjects.noSubjects')} forcedHeight='30vh' />
                ) : (
                  <>
                    <div className={styles.header}>
                      <div className={styles.checkbox}>
                        <Checkbox
                          onChange={(event) => {
                            handleSelectAll(event.target.checked);
                          }}
                          checked={
                            filteredModel.length > 0 &&
                            filteredModel.filter((t) => t.selected).length === filteredModel.length
                          }
                          name={`toggle-subjects-all`}
                        />
                        <div>{t('planungImport.subjectInPlanung')}</div>
                      </div>
                      <div></div>
                      <div>{t('subjects.globalSubject')}</div>
                      <div>{t('planungImport.subjectInBp')}</div>
                      <div>{t('planungImport.connected')}</div>
                    </div>
                    {filteredModel.map((ps) => (
                      <PlanungImportSubjectsFormRow
                        key={ps.uuid}
                        uuid={ps.uuid}
                        selected={ps.selected}
                        name={ps.name}
                        globalSubjects={availableGlobalSubjects}
                        subjects={availableSubjects}
                        selectedGlobal={
                          ps.connectGlobalName && globalSubjects.find((gs) => gs.name === ps.connectGlobalName)
                            ? {
                                value: globalSubjects.find((gs) => gs.name === ps.connectGlobalName)?.name ?? '',
                                label: globalSubjects.find((gs) => gs.name === ps.connectGlobalName)?.name ?? '',
                              }
                            : null
                        }
                        selectedSubject={
                          ps.connectSubjectUuid && subjects.find((s) => s.uuid === ps.connectSubjectUuid)
                            ? {
                                value: subjects.find((s) => s.uuid === ps.connectSubjectUuid)?.uuid ?? '',
                                label: subjects.find((s) => s.uuid === ps.connectSubjectUuid)?.name ?? '',
                              }
                            : null
                        }
                        connected={ps.connected}
                        onSelect={(isSelected) => handleSelect(ps.uuid, isSelected)}
                        onGlobalSelect={(subjectUuid) => handleGlobalSelect(ps.uuid, subjectUuid)}
                        onSubjectSelect={(subjectUuid) => handleSubjectSelect(ps.uuid, subjectUuid)}
                      />
                    ))}
                  </>
                )}
              </div>
            </BpCard>
          )}
        </Form>
      )}
    </Formik>
  );
};
